import { useState, useEffect } from "react";
import React = require("react");
import { AmorgRole, rng } from '../../../amorg-common';
import { CommandResult } from '../../../amorg-common';
import * as dateMath from 'date-arithmetic';
import { H2 } from "../../../designSystem/typography/Heading";
import { Label } from "../../../designSystem/Label";
import { ButtonRow } from "../../../designSystem/button/ButtonRow";
import { Button } from "../../../designSystem/button/Button";
import { SelectField } from "../../../designSystem/formField/SelectField";
import { Flex } from '../../../designSystem/compositeComponents/FlexHelpers';
import { DateInputField } from "../../../designSystem/formField/DateInputField";
import { CheckboxField } from "../../../designSystem/formField/CheckboxField";
import { getRoles, getUserRoles, createUserRole, deleteUserRole, updateUserRole } from "../../../api/userClientApi";
import { AmorgUserRole } from '../../../amorg-common/DTOs/AmorgUserRole';
import { P } from "../../../designSystem/typography/Body";
import { Modal } from "../../../designSystem/Modal";
import { AmorgMenu, AmorgMenuContent } from "../../../designSystem/amorgMenu";
import { Card } from "../../../designSystem/Card";


type RoleEditorProps = {
    userId: string;
    disabled?:boolean
};
export function RoleEditor ({ userId, disabled }: RoleEditorProps): JSX.Element
{

    const [roles, setRoles] = useState<AmorgRole[]>([]);
    const [userRoles, setUserRoles] = useState<AmorgUserRole[]>([]);
    const [inEditUserRole, setInEditUserRole] = useState<AmorgUserRole>(undefined);

    useEffect(() =>
    {
        const getData = async () =>
        {
            setRoles(await getRoles());
        };
        getData();
    }, []);

    useEffect(() =>
    {
        const getData = async () =>
        {
            setUserRoles(await getUserRoles(userId));
        };
        getData();
    }, []);

    async function handleClickAddRole ()
    {
        const id = rng.getRandomId();
        const today = new Date();
        today.setHours(0, 0, 0);
        const result = await createUserRole(id, userId, 'work_environment_representative', today, dateMath.add(today, 1, 'year'), userRoles.find(ur => ur.isCurrentRole) === undefined);
        if (result.isSuccess)
        {
            const userRoles = await getUserRoles(userId);
            setUserRoles(userRoles);
            setInEditUserRole(userRoles.find(ur => ur.id === id));
        }
    }

    async function handleClickEditRole (userRoleId: string)
    {
        setInEditUserRole(userRoles.find(ur => ur.id === userRoleId));
    }

    async function handleClickDeleteRole (userRoleId: string)
    {
        await deleteUserRole(userId, userRoleId);
        setUserRoles(await getUserRoles(userId));
        setInEditUserRole(undefined);
    }

    async function handleUpdate ()
    {
        const userRoles = await getUserRoles(userId);
        setUserRoles(userRoles);
        setInEditUserRole(userRoles.find(ur => ur.id === inEditUserRole.id));
    }

    const activeRole = userRoles.find(ur => ur.isCurrentRole);
    const inactiveRoles = userRoles.filter(ur => !ur.isCurrentRole);


    return (
        <>

            <Modal
                title={"Rediger rolle"}
                content={inEditUserRole &&
                    <EditRolePanel roles={roles} userId={userId} userRole={inEditUserRole}
                        onUpdated={handleUpdate}
                        onDelete={() => handleClickDeleteRole(inEditUserRole.id)}
                        onClose={() => setInEditUserRole(undefined)} />
                }
                showModal={inEditUserRole !== undefined}
                onCancel={() => setInEditUserRole(undefined)} />

            <Card variant="outlined">
                <Flex flexDirection="column" gap={4}>
                    <H2>Rolle</H2>

                    {!activeRole &&
                        <RoleSection active={false} title="Aktiv rolle">
                            <Flex alignItems="center" style={{ height: '40px' }}>
                                {roles.length === 0
                                    ? <P>Ingen roller oprettet</P>
                                    : <P>Ingen aktiv rolle valgt</P>
                                }
                            </Flex>
                        </RoleSection>
                    }

                    {activeRole &&
                        <RoleSection active={true} title="Aktiv rolle">
                            <RoleListingItem userId={userId} userRole={activeRole} 
                                onClickEdit={disabled ? undefined : handleClickEditRole} 
                                onClickDelete={disabled ? undefined : handleClickDeleteRole} />
                        </RoleSection>
                    }

                    {inactiveRoles.length > 0 &&

                        <RoleSection active={false} title="Inaktive roller">
                            <Flex flexDirection="column" gap={4}>
                                {inactiveRoles.map((r, index) =>
                                    <RoleListingItem key={index} userId={userId} userRole={r} 
                                        onClickEdit={disabled ? undefined : handleClickEditRole} 
                                        onClickDelete={disabled ? undefined : handleClickDeleteRole} />
                                )}
                            </Flex>
                        </RoleSection>
                    }

                    <ButtonRow>
                        <Button
                            variant="outlined"
                            onClick={handleClickAddRole}
                            text={"Tilføj ny rolle"}
                            disabled={disabled} />
                    </ButtonRow>

                </Flex>
            </Card>
        </>
    );
}

type RoleSectionProps = {
    title: string,
    active: boolean,
    children: React.ReactNode;

};
function RoleSection (props: RoleSectionProps): JSX.Element
{

    return (
        <Flex flexDirection="column" gap={4}>
            <Label>{props.title}</Label>
            {props.children}
        </Flex>
    );
}


type RoleListingItemProps = {
    userId: string;
    userRole: AmorgUserRole;
    onClickDelete: (userRoleId: string) => void;
    onClickEdit: (userRoleId: string) => void;
};
function RoleListingItem (props: RoleListingItemProps): JSX.Element
{

    const handleClickEdit = () =>
        props.onClickEdit(props.userRole.id);

    const formatDate = (date: Date) =>
    {
        let dt = date;
        if (typeof (dt) === 'string')
            dt = new Date(dt);
        return `${dt.getDate()}/${dt.getMonth() + 1}-${dt.getFullYear()}`;
    };


    const itm: AmorgMenuContent | undefined = {

        type: 'leadingContent',
        onClick: () => handleClickEdit(),
        leadingContent: { type: 'icon', iconName: "interface_user_user" },
        primaryContent: { type: 'textWithSubtext', text: props.userRole.title, subtext: `${formatDate(props.userRole.start)} - ${formatDate(props.userRole.end)}` },
        styleVariant: status === "alert" ? "destructive" : "default"
    };

    return (
        <AmorgMenu content={[itm]} />
    );
}

type EditRolePanelProps = {
    roles: AmorgRole[],
    userId: string,
    userRole: AmorgUserRole,
    onUpdated: () => void,
    onDelete: () => void,
    onClose: () => void;
};
function EditRolePanel ({ roles, userId, userRole, onUpdated, onDelete, onClose }: EditRolePanelProps): JSX.Element
{

    function handleResult (result: CommandResult)
    {
        if (result.isSuccess)
            onUpdated();
    }

    async function handleRoleChange (newValue: string)
    {
        handleResult(
            await updateUserRole(userRole.id, userId, newValue, userRole.start, userRole.end, userRole.isCurrentRole)
        );
    }

    async function handleStartChange (value: Date)
    {
        handleResult(
            await updateUserRole(userRole.id, userId, userRole.roleId, value, userRole.end, userRole.isCurrentRole)
        );
    }

    async function handleEndChange (value: Date)
    {
        handleResult(
            await updateUserRole(userRole.id, userId, userRole.roleId, userRole.start, value, userRole.isCurrentRole)
        );
    }

    async function handleIsCurrentRoleChange (value: string, checked: boolean)
    {
        handleResult(
            await updateUserRole(userRole.id, userId, userRole.roleId, userRole.start, userRole.end, checked)
        );
    }

    return (
        <Flex flexDirection="column" gap={164}>
            <Flex flexDirection="column" gap={16}>
                <SelectField
                    id="role-selector"
                    label="Rolle"
                    options={roles.map(r => new Option(r.title, r.id))}
                    selectedValue={userRole.roleId}
                    onChange={handleRoleChange} />

                <Flex justifyContent="space-between" gap={5}>
                    <DateInputField
                        id="role-start"
                        label="Fra"
                        width="100%"
                        value={new Date(userRole.start)}
                        onValueChange={handleStartChange}
                        validator={() => { return { valid: true }; }} />

                    <DateInputField
                        id="role-end"
                        label="Til"
                        width="100%"
                        value={new Date(userRole.end)}
                        onValueChange={handleEndChange}
                        validator={() => { return { valid: true }; }} />
                </Flex>

                <CheckboxField
                    id="isCurrent"
                    label="Er det den aktuelle rolle?"
                    text=""
                    checked={userRole.isCurrentRole}
                    value="isCurrent"
                    onChange={handleIsCurrentRoleChange} />
            </Flex>
            <Flex flexDirection="row" justifyContent="space-between">
                <Button variant="destructive"
                    onClick={onDelete}
                    icon={"interface_actions_delete"}
                    text={"Slet"}
                />

                <Button variant="filled"
                    onClick={onClose}
                    icon={"actions_close"}
                    text={"Luk"}
                />
            </Flex>
        </Flex>
    );
}